import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Gauge, Fuel, Settings2, User, Calendar, Car as CarIcon, Phone, MessageCircle, Share2 } from "lucide-react";
import { api, imageUrl, formatPrice, formatKms, whatsappLink, shareUrl, PHONE } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";

const CarDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const [car, setCar] = useState(null);
  const [active, setActive] = useState(0);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/cars/${id}`).then((res) => {
      setCar(res.data);
      document.title = `${res.data.brand} ${res.data.model} ${res.data.year} | Shree Peteanjaneya Swamy Enterprises`;
    }).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-5" data-testid="car-not-found">
        <p className="text-gray-400">{t("car.unavailable")}</p>
        <Link to="/inventory"><Button className="bg-[#FF3B30] hover:bg-[#FF5247]">{t("car.backToInv")}</Button></Link>
      </div>
    );
  }

  if (!car) return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading...</div>;

  const specs = [
    { icon: Calendar, label: t("car.year"), value: car.year },
    { icon: User, label: t("car.ownership"), value: car.ownership },
    { icon: Settings2, label: t("car.transmission"), value: car.transmission },
    { icon: Fuel, label: t("car.fuel"), value: car.fuel_type },
    { icon: Gauge, label: t("car.kms"), value: formatKms(car.kilometers) },
    { icon: CarIcon, label: t("car.brand"), value: car.brand },
  ];

  const enquiryText = `Hi, I'm interested in the ${car.brand} ${car.model} (${car.year}) priced at ${formatPrice(car.price)}. Is it available?`;

  const handleShare = async () => {
    const link = shareUrl(car.id);
    const shareText = `${car.brand} ${car.model} ${car.year} — ${formatPrice(car.price)}\n${link}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: `${car.brand} ${car.model}`, text: `${car.brand} ${car.model} ${car.year} — ${formatPrice(car.price)}`, url: link });
        return;
      } catch (e) {
        console.debug("Native share dismissed/unavailable, using WhatsApp", e);
      }
    }
    window.open(whatsappLink(shareText), "_blank");
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-24 pb-24 min-h-screen">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-400 hover:text-white text-sm mb-6 transition-colors" data-testid="back-btn">
        <ArrowLeft size={16} /> {t("car.back")}
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="aspect-video bg-black rounded-xl overflow-hidden border border-white/10 relative">
            {car.images?.length ? (
              <img src={imageUrl(car.images[active])} alt={`${car.brand} ${car.model}`} className="w-full h-full object-cover" data-testid="detail-main-image" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-600">{t("car.noImage")}</div>
            )}
            {car.is_sold && (
              <span className="absolute top-4 right-4 bg-[#FF3B30] text-white text-sm font-black px-4 py-1.5 rounded-full shadow-lg tracking-wider" data-testid="detail-sold-badge">
                {t("car.sold")}
              </span>
            )}
          </div>
          {car.images?.length > 1 && (
            <div className="flex gap-3 mt-4 flex-wrap">
              {car.images.map((img, i) => (
                <button key={img} onClick={() => setActive(i)} className={`w-20 h-16 rounded-md overflow-hidden border-2 ${active === i ? "border-[#FF3B30]" : "border-white/10"}`} data-testid={`thumb-${i}`}>
                  <img src={imageUrl(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-xs tracking-[0.2em] uppercase text-gray-500 font-bold">{car.brand}</p>
          <h1 className="font-display font-black text-3xl sm:text-4xl text-white mt-1" data-testid="detail-title">{car.brand} {car.model}</h1>
          <p className="text-[#FF3B30] font-display font-black text-4xl mt-3" data-testid="detail-price">{formatPrice(car.price)}</p>

          <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10 rounded-xl overflow-hidden mt-8">
            {specs.map((s) => (
              <div key={s.label} className="bg-[#141414] p-5">
                <span className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-500 font-bold"><s.icon size={14} /> {s.label}</span>
                <p className="text-white font-semibold mt-1.5">{s.value}</p>
              </div>
            ))}
          </div>

          {car.description && (
            <div className="mt-6">
              <p className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-2">{t("car.description")}</p>
              <p className="text-gray-300 text-sm leading-relaxed">{car.description}</p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 mt-8">
            <a href={whatsappLink(enquiryText)} target="_blank" rel="noreferrer" className="flex-1">
              <Button className="w-full bg-[#25D366] hover:bg-[#1eb858] text-black font-bold h-12 rounded-md" data-testid="detail-whatsapp"><MessageCircle size={18} className="mr-2" /> {t("car.enquire")}</Button>
            </a>
            <a href={`tel:${PHONE}`} className="flex-1">
              <Button variant="outline" className="w-full border-white/20 bg-white/5 hover:bg-white/10 text-white font-bold h-12 rounded-md" data-testid="detail-call"><Phone size={18} className="mr-2" /> {t("car.call")} {PHONE}</Button>
            </a>
          </div>
          <Button onClick={handleShare} variant="outline" className="w-full mt-4 border-white/20 bg-white/5 hover:bg-white/10 text-white font-bold h-12 rounded-md" data-testid="detail-share">
            <Share2 size={18} className="mr-2" /> {t("car.share")}
          </Button>
        </motion.div>
      </div>
    </div>
  );
};

export default CarDetail;
